import { mount, unmount } from 'svelte';
import WidgetApp from './components/WidgetApp.svelte';
import { t, loadLocale, isLocaleReady } from './i18n/index';
import { createWidgetStyles } from './styles/widget-styles';
import { autoApplyCachedAltTexts } from './features/alt-text';
import { isPreviewContext } from './preview-url';
import type { AccessifyConfig } from './types';

// 'spacing' und 'saturation' sind wieder aktive Features (Text-Abstände nach
// WCAG 1.4.12 bzw. Sättigung/Graustufen). Weiterhin entfernt: die reine
// Farbenblind-SIMULATION (macht Farben für Betroffene schlechter, kein
// Endnutzer-Mehrwert), Einzel-Regler text-align/line-height (in spacing
// aufgegangen) und der alte Auto-Scan.
const REMOVED_FEATURES = new Set([
  'color-blind',
  'color-blindness',
  'text-align',
  'line-height',
  'focus-highlight',
  'auto-scan',
]);

function detectLang(): string {
  const htmlLang = document.documentElement.lang?.trim();
  if (htmlLang) return htmlLang.split('-')[0].toLowerCase();
  const nav = (navigator.language || (navigator as any).userLanguage || '').trim();
  if (nav) return nav.split('-')[0].toLowerCase();
  return 'en';
}

const DEFAULT_CONFIG: AccessifyConfig = {
  position: 'bottom-right',
  theme: 'auto',
  lang: detectLang(),
  // Kuratierter Default: solide Grundausstattung über alle Bedarfsgruppen
  // (Sehen, Lesen/Fokus, Motorik, Reizempfindlichkeit, Kognition). Neu im
  // Default: 'spacing' (Text-Abstände, WCAG 1.4.12), 'dyslexia-font'
  // (Lesefreundliche Schrift), 'saturation' (Sättigung/Graustufen).
  // Verfügbar, aber nur im Dashboard zuschaltbar (nicht vorbelegt): 'magnifier',
  // 'page-zoom', 'mute-media', 'cursor-highlight', 'reading-guide' (redundant zur
  // Lesemaske), 'page-structure', 'alt-text' (KI-Bildbeschreibung).
  // big-cursor + keyboard-nav werden auf Touch-Geräten automatisch ausgeblendet.
  features: [
    'contrast', 'text-size', 'spacing', 'link-highlight', 'reading-mask',
    'saturation', 'dyslexia-font', 'hide-images', 'animation-stop',
    'big-cursor', 'keyboard-nav', 'tts', 'text-simplify',
  ],
  zIndex: 2147483647,
  compact: false,
};

let widgetInstance: any = null;
let containerEl: HTMLElement | null = null;
let config: AccessifyConfig = { ...DEFAULT_CONFIG };

// ─── EyeAble-style "hide widget for me" ──────────────────────────────────────
// A visitor can dismiss the whole widget on this site; the choice persists per
// origin in localStorage. The footer shows an explanatory CONFIRMATION first
// (PanelFooter) — the actual hide only fires after the visitor confirms.
//
// Intentionally there is NO keyboard / skip-link path back: once hidden, the
// ONLY way to restore the widget is to clear the site's browser data (which
// drops the localStorage flag). So when hidden we also tear the skip-link down
// (no dead affordance) and openWidget() refuses to act.
//
// The owner can switch the capability off per site (allowVisitorHide === false)
// — a stale hidden flag is then ignored so the widget reappears for everyone.
const HIDDEN_KEY = 'accessify-hidden';
// Flips true once a trusted Dashboard preview message arrives. Inside the
// preview iframe the hide button stays visible (so the owner sees it) but is
// inert — clicking it must never blank the live preview.
let isPreviewMode = false;

/** Localhost / dev: the "hide" opt-out must NOT persist or block the widget.
 *  On a real site a visitor's dismissal is sticky by design, but on localhost
 *  a single test click would lock the widget out until you manually clear
 *  storage (no keyboard path back). So on localhost we ignore the stored flag
 *  on read and skip the write — production keeps the real, sticky behaviour. */
function isLocalhostDev(): boolean {
  try {
    const h = window.location.hostname;
    if (h === 'localhost' || h === '127.0.0.1' || h === '0.0.0.0') return true;
    if (h.endsWith('.localhost') || h.endsWith('.local')) return true;
    // Private LAN ranges too — so testing on a real phone via the Mac's LAN IP
    // (e.g. http://192.168.x.x:3000) is also treated as dev and the "hide"
    // opt-out never sticks there either.
    return /^(10\.|192\.168\.|172\.(1[6-9]|2\d|3[01])\.)/.test(h);
  } catch { return false; }
}

function isWidgetHidden(): boolean {
  if (isLocalhostDev()) return false;
  try { return localStorage.getItem(HIDDEN_KEY) === '1'; } catch { return false; }
}

function setWidgetHidden(hidden: boolean): void {
  if (!isLocalhostDev()) {
    try {
      if (hidden) localStorage.setItem(HIDDEN_KEY, '1');
      else localStorage.removeItem(HIDDEN_KEY);
    } catch { /* storage blocked — still toggle the visual below */ }
  }
  // Toggle a host attribute that the shadow's :host([data-accessify-hidden])
  // rule keys off. We can't hide via an outer inline display:none here: per the
  // CSS Scoping spec an !important :host{display:block} from inside the shadow
  // tree beats an outer inline !important, so the attribute path is the only one
  // that actually wins. See widget-styles.ts.
  containerEl?.toggleAttribute('data-accessify-hidden', hidden);
  if (hidden) {
    // The visitor opted out entirely — there is no key/skip-link path back, so
    // tear down the skip-link nav and its re-insert observer. Otherwise a
    // keyboard user would tab to a "skip to accessibility settings" link that
    // does nothing.
    try { (window as any).__accessify_skip_observer?.disconnect(); } catch { /* ignore */ }
    document.getElementById('accessify-skip-nav')?.remove();
  }
}

/**
 * Fetch per-site config from the API if a siteKey is present.
 * Merges server config under user config (data-attributes take precedence).
 */
async function fetchSiteConfig(siteKey: string, proxyUrl: string): Promise<Partial<AccessifyConfig>> {
  try {
    const base = proxyUrl || 'https://api.goaccessify.de';
    // Cache-bust per 5-minute bucket so a Dashboard save is visible on the
    // next reload at the latest. Without this the worker response sits in
    // browser/CDN cache and brand-color changes appear "not to take".
    const bucket = Math.floor(Date.now() / 300000); // 5 min
    const res = await fetch(`${base}/v1/config/${siteKey}?t=${bucket}`, {
      headers: { 'Accept': 'application/json' },
      cache: 'no-store',
    });
    if (!res.ok) return {};
    const data = await res.json();
    const serverCfg = data?.config || {};

    // Map server config fields to AccessifyConfig fields.
    //
    // R13-1: `features.length` ist falsy bei `[]` — ein leerer Array bedeutet
    // ABER „User hat im Dashboard alles abgewählt", nicht „nicht gesetzt".
    // Wir prüfen mit `Array.isArray()` und akzeptieren auch das leere Array
    // als explizite Konfiguration. Sonst überschreibt der Default (alle 13
    // Features) die User-Entscheidung „nichts an".
    const mapped: Partial<AccessifyConfig> & Record<string, any> = {};
    if (Array.isArray(serverCfg.features)) mapped.features = serverCfg.features;
    if (serverCfg.defaultLang) mapped.lang = serverCfg.defaultLang;
    if (serverCfg.position) mapped.position = serverCfg.position;
    if (serverCfg.theme) mapped.theme = serverCfg.theme;
    if (serverCfg.compact != null) mapped.compact = serverCfg.compact;
    // Branding colors (passed through to styles)
    const brandFields = [
      'brandPrimaryColor', 'brandSurfaceColor', 'brandTextColor', 'brandBorderColor',
      'brandCardColor', 'brandAccentColor', 'brandTextDimColor',
      'brandTriggerColor', 'brandTriggerIconColor', 'brandHoverColor',
    ];
    for (const field of brandFields) {
      if (serverCfg[field]) mapped[field] = serverCfg[field];
    }
    if (serverCfg.panelWidth) mapped.panelWidth = serverCfg.panelWidth;
    if (serverCfg.simplificationLevel) mapped.simplificationLevel = serverCfg.simplificationLevel;
    if (typeof serverCfg.textScalePercent === 'number') mapped.textScalePercent = serverCfg.textScalePercent;
    // EyeAble-style self-dismiss capability — owner toggle in the Dashboard.
    // Only a real boolean overrides the default; absent leaves it "on".
    if (typeof serverCfg.allowVisitorHide === 'boolean') mapped.allowVisitorHide = serverCfg.allowVisitorHide;
    // aiAvailable steht auf der Top-Level-Antwort (nicht in .config): meldet, ob
    // diese Site einen AI-Provider-Key hat. Steuert das Ein-/Ausblenden der
    // KI-Features weiter unten im Feature-Filter.
    if (typeof data.aiAvailable === 'boolean') mapped.aiAvailable = data.aiAvailable;
    return mapped;
  } catch {
    // Silently fail - widget works without server config
    return {};
  }
}

/**
 * Inject the brand font stylesheet (Inter Display + Switzer via Fontshare)
 * into the host page's <head> exactly once. The widget panel + every feature
 * overlay then renders in the same typography as the Dashboard — without
 * bundling ~120 KB of woff2 into the widget itself.
 *
 * Idempotent across multiple init() calls and across multiple widget
 * instances on the same page (rare, but cheap to guard).
 *
 * Fails silently when Fontshare is blocked / offline: the font-family chain
 * in widget-styles.ts falls back to "Inter Display Placeholder" /
 * "Switzer Placeholder" / -apple-system, so the widget stays readable.
 */
/**
 * R15-3 / R15-8 Fix: Externer Fontshare-Stylesheet-Inject war doppelt broken
 * — CSP `style-src 'self'` blockte ihn auf Enterprise/Gov-Sites, und Font-
 * shares CORS-Header `Access-Control-Allow-Origin: https://api.fontshare.com`
 * blockte ihn auf jeder anderen Origin. Im Widget-Styles-Stack fallen Inter
 * Display und Switzer auf System-Defaults zurück (`-apple-system, …`),
 * d. h. Funktion intakt, Visual-Regression akzeptabel.
 *
 * TODO (optional, nicht R15-Scope): woff2-Files lokal ins Widget-Bundle
 * einbetten (Vite `?inline` + `@font-face data:font/woff2;base64,…`). Erst
 * dann sind Inter/Switzer wieder garantiert — auch ohne CDN-Round-Trip.
 */
function injectBrandFonts(): void {
  // No-op: siehe R15-3 / R15-8. Widget-Styles haben System-Font-Fallback.
  return;
}

/**
 * Inject a visually-hidden skip-link as the first focusable element in the
 * host body. Tabbing into the page reveals it; pressing Enter moves focus
 * into the widget shadow root's trigger button and opens the panel.
 *
 * Why this exists: even with `delegatesFocus: true` on the shadow root,
 * Chromium does not automatically put the shadow host into the natural tab
 * order — the trigger button inside the shadow is therefore unreachable by
 * keyboard. Adding `tabindex="0"` on the host (see init() below) fixes the
 * basic case, but the host is appended at the end of <body>, so screen-
 * reader + keyboard users would still have to tab past every link/form
 * control on the page before they can open the accessibility panel. The
 * skip-link is the conventional pattern (also used by gov.uk and every
 * major a11y overlay) for exactly this scenario.
 *
 * The style block lives in the host <head>, not the shadow root, because
 * the link itself must live in the host DOM — otherwise the browser's tab
 * order would skip it the same way it currently skips the trigger.
 *
 * Idempotent across multiple init() calls.
 */
/**
 * Open the widget panel programmatically. Used by the skip-link and
 * anywhere else we want to give the user a one-step path to the panel
 * without forcing them to find the trigger by eye.
 *
 * Implementation note: the TriggerButton uses pointerdown/up listeners
 * for its click behaviour (so it can distinguish a click from a drag),
 * which means a synthetic `trigger.click()` is ignored — `.click()` does
 * not synthesize pointer events. We simulate a real pointer tap on the
 * trigger so the existing pointerup handler fires the widget's
 * `toggleOpen()` (which keeps the Svelte `isOpen` state in sync — and
 * therefore makes ESC close the panel as expected).
 */
function openWidget(): void {
  // If the visitor hid the widget, there is deliberately NO way back via the
  // keyboard / skip-link — only clearing the site's browser data restores it.
  // So a hidden widget refuses to open here.
  if (isWidgetHidden()) return;
  const root = document.getElementById('accessify-root')
    || document.querySelector('[data-accessify-root="true"]') as HTMLElement | null;
  const shadow = (root as HTMLElement | null)?.shadowRoot;
  if (!shadow) return;
  const trigger = shadow.querySelector('.accessify-trigger') as HTMLElement | null;
  if (!trigger) return;

  // If the panel is already open, just focus it.
  const panel = shadow.querySelector('.accessify-panel') as HTMLElement | null;
  if (panel?.getAttribute('aria-hidden') === 'false') {
    const first = panel.querySelector<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    first?.focus();
    return;
  }

  // Simulate a pointer tap on the trigger: the TriggerButton's pointerup
  // handler runs toggleOpen() iff hasMoved === false, which is the case
  // for a same-position down/up pair. This routes through the widget's
  // own open() (sets isOpen=true, clamps panel, focuses first control)
  // so ESC and the full state machine stay consistent.
  const rect = trigger.getBoundingClientRect();
  const x = rect.left + rect.width / 2;
  const y = rect.top + rect.height / 2;
  const downOpts = { bubbles: true, composed: true, cancelable: true, clientX: x, clientY: y, pointerId: 1, pointerType: 'mouse', button: 0 };
  trigger.dispatchEvent(new PointerEvent('pointerdown', downOpts));
  trigger.dispatchEvent(new PointerEvent('pointerup', downOpts));

  // Fallback: if the pointer-event path was blocked (e.g. shadow root
  // policy quirk in some embedded webviews), flip the attribute directly
  // so the user at least sees the panel.
  setTimeout(() => {
    if (panel && panel.getAttribute('aria-hidden') === 'true') {
      panel.setAttribute('aria-hidden', 'false');
    }
    const triggerNow = shadow.querySelector('.accessify-trigger') as HTMLElement | null;
    triggerNow?.focus();
  }, 50);
}

function injectSkipLink(lang: string): void {
  if (typeof document === 'undefined') return;
  const ID = 'accessify-skip-link';
  const NAV_ID = 'accessify-skip-nav';

  const STYLE_ID = 'accessify-skip-link-style';
  if (!document.getElementById(STYLE_ID)) {
    const style = document.createElement('style');
    style.id = STYLE_ID;
    // Minified inline — the CSS string sits in the widget bundle and every
    // saved byte counts against the 80 KB gzip budget.
    style.textContent = '.accessify-skip-link{position:fixed!important;top:-100px;left:16px;z-index:2147483646;padding:12px 18px;background:#101011;color:#fff;font:600 14px/1.4 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;text-decoration:none;border-radius:8px;box-shadow:0 4px 14px rgba(0,0,0,.25);transition:top 150ms ease-out}.accessify-skip-link:focus{top:16px;outline:3px solid #c2473d;outline-offset:2px;box-shadow:0 0 0 2px #fff,0 4px 14px rgba(0,0,0,.25)}';
    (document.head || document.documentElement).appendChild(style);
  }

  function buildNav(): HTMLElement {
    const a = document.createElement('a');
    a.id = ID;
    a.className = 'accessify-skip-link';
    a.href = '#';
    a.textContent = lang.startsWith('de')
      ? 'Zu den Barrierefreiheits-Einstellungen springen'
      : 'Skip to accessibility settings';
    a.addEventListener('click', (e) => {
      e.preventDefault();
      openWidget();
    });
    // R9-3: wrap in a `<nav>` landmark so axe's `region` rule stops
    // flagging the link as "content outside any landmark".
    const n = document.createElement('nav');
    n.id = NAV_ID;
    n.setAttribute('aria-label', lang.startsWith('de') ? 'Barrierefreiheit' : 'Accessibility');
    n.appendChild(a);
    return n;
  }

  function ensureAtTop() {
    let nav = document.getElementById(NAV_ID);
    if (!nav) {
      nav = buildNav();
      document.body.insertBefore(nav, document.body.firstChild);
      return;
    }
    if (document.body.firstChild !== nav) {
      // Something — typically a React hydrate, a Framer client-component
      // mount, or a Wordpress theme that prepends a banner — pushed our
      // skip-link past first-child position. Move it back so it stays
      // the first tab stop (R9-1: this is what was killing keyboard
      // reach on production).
      document.body.insertBefore(nav, document.body.firstChild);
    }
  }

  ensureAtTop();

  // R9-1: Framer (and other React-driven Single-Page-Apps) re-render the
  // body on hydrate. Without an observer, our nav is appended once and
  // then removed by the next render pass — and a blind keyboard user
  // tabs through 80 stops without ever reaching the widget. The observer
  // re-inserts the nav on every body-child mutation, throttled so it
  // does not fight a host page that legitimately keeps prepending.
  if (!(window as any).__accessify_skip_observer) {
    let scheduled = false;
    const obs = new MutationObserver(() => {
      if (scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        ensureAtTop();
      });
    });
    obs.observe(document.body, { childList: true });
    (window as any).__accessify_skip_observer = obs;
  }
}

/**
 * After the visitor hides the widget, surface a clear, visible notice: the
 * ONLY way back is clearing this site's browser data. There is deliberately
 * no keyboard re-show shortcut anymore (Alt+0 was unreliable across browsers
 * and never restored a hidden widget). Rendered into the page body, NOT the
 * now-hidden shadow host, as a polite live-region toast with a close button
 * and a generous auto-dismiss.
 */
function showHiddenToast(): void {
  if (typeof document === 'undefined') return;
  const TOAST_ID = 'accessify-hidden-toast';
  document.getElementById(TOAST_ID)?.remove();
  const lang = (config?.lang as string) || 'en';

  const el = document.createElement('div');
  el.id = TOAST_ID;
  el.setAttribute('role', 'status');
  el.setAttribute('aria-live', 'polite');
  el.style.cssText =
    'position:fixed;left:50%;bottom:24px;transform:translateX(-50%);' +
    'z-index:2147483647;max-width:380px;box-sizing:border-box;display:flex;' +
    'gap:12px;align-items:flex-start;padding:14px 16px;background:#101011;' +
    'color:#fff;border-radius:12px;box-shadow:0 8px 28px rgba(0,0,0,.28);' +
    'font:400 14px/1.5 -apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;';

  const msg = document.createElement('span');
  msg.style.cssText = 'flex:1';
  msg.textContent = t('widget.hiddenToast', lang);

  const close = document.createElement('button');
  close.type = 'button';
  close.textContent = '✕';
  close.setAttribute('aria-label', t('widget.hiddenToastClose', lang));
  close.style.cssText =
    'flex:none;background:transparent;border:0;color:#fff;font-size:16px;' +
    'line-height:1;cursor:pointer;padding:2px;opacity:.85;';

  const dismiss = () => el.remove();
  close.addEventListener('click', dismiss);
  el.appendChild(msg);
  el.appendChild(close);
  document.body.appendChild(el);
  window.setTimeout(dismiss, 12000);
}

async function init(userConfig: Partial<AccessifyConfig> = {}) {
  if (widgetInstance) return;

  const dataConfig = (window as any).__accessify_dataConfig || {};

  // R13-2: Merge-Reihenfolge umgekehrt — Dashboard-Server-Konfig ist die
  // Quelle der Wahrheit (Marketing/Owner soll im Dashboard alles steuern,
  // ohne Code anzufassen). Data-Attribute am <script>-Tag liefern nur das
  // Embed-spezifische Minimum: `siteKey`, `proxyUrl`, optional `lang` für
  // sprachverschiedene Sub-Seiten derselben Site.
  const EMBED_OVERRIDABLE = new Set(['siteKey', 'proxyUrl', 'lang', 'disableFontInjection']);
  function applyEmbedOverrides(base: AccessifyConfig, embed: Record<string, any>): AccessifyConfig {
    const next = { ...base } as Record<string, any>;
    for (const key of Object.keys(embed)) {
      if (EMBED_OVERRIDABLE.has(key) && embed[key] != null) next[key] = embed[key];
    }
    return next as AccessifyConfig;
  }

  // Baseline ohne Server: defaults + Embed-Overridable-Subset
  config = applyEmbedOverrides({ ...DEFAULT_CONFIG }, { ...userConfig, ...dataConfig });

  if (!config.disableFontInjection) {
    injectBrandFonts();
  }

  // Mit siteKey: Dashboard-KV-Config gewinnt über alles außer Embed-Allowlist.
  const siteKey = config.siteKey || (config as any).siteKey;
  const proxyUrl = config.proxyUrl || (config as any).proxyUrl;
  if (siteKey) {
    const serverConfig = await fetchSiteConfig(siteKey, proxyUrl || '');
    // DEFAULT < serverConfig (Dashboard wins!) < Embed-Allowlist (lang etc.)
    const baseWithServer = { ...DEFAULT_CONFIG, ...serverConfig };
    config = applyEmbedOverrides(baseWithServer as AccessifyConfig, { ...userConfig, ...dataConfig });
  }

  // Warn (don't fail) when the caller asked for a feature we removed in a
  // past release. Helpful for site owners who copy-paste old snippets:
  // without this warn, `data-features="dyslexia-font,contrast"` silently
  // drops `dyslexia-font` and the owner has no idea why.
  const requestedFeatures = config.features || DEFAULT_CONFIG.features;
  const droppedFeatures = requestedFeatures.filter((feature) => REMOVED_FEATURES.has(feature));
  if (droppedFeatures.length > 0) {
    console.warn(
      '[Accessify] These features are no longer supported and were skipped:',
      droppedFeatures
    );
  }
  let activeFeatures = requestedFeatures.filter((feature) => !REMOVED_FEATURES.has(feature));

  // KI-Gating: Vereinfachen + Alt-Texte laufen ausschließlich über den Proxy
  // (/v1/ai mit X-Site-Key) und brauchen einen im Dashboard hinterlegten
  // Provider-Key. Der frühere Direct-Key-Pfad (data-openrouter-key) ist bewusst
  // stillgelegt: applyEmbedOverrides reicht openRouterKey nicht mehr durch.
  // Meldet der Server aiAvailable === false (z. B. frische Shopify-Installation
  // ohne Key), blenden wir die Features aus, statt kaputte Buttons zu zeigen,
  // die am 403 scheitern. Sie erscheinen automatisch, sobald im Dashboard ein
  // Key gesetzt ist (Config-Reload). Das macht den fehlenden Key zum Dashboard-
  // Köder (Funnel) statt zur Fehlfunktion.
  if (config.aiAvailable === false) {
    const AI_FEATURES = new Set(['text-simplify', 'alt-text']);
    activeFeatures = activeFeatures.filter((feature) => !AI_FEATURES.has(feature));
  }
  config.features = activeFeatures;

  containerEl = document.createElement('div');
  // Defend against an id collision: if the host page already owns
  // `#accessify-root` (e.g. a marketing page showcasing the widget), the
  // widget-entry idempotency guard (`document.getElementById('accessify-root')`)
  // would mis-detect that as "already initialized" and silently no-op. Fall
  // back to a random suffix and tag the element with `data-accessify-root`
  // so internal selectors stay reliable.
  let rootId = 'accessify-root';
  const existingRoot = document.getElementById(rootId);
  if (existingRoot) {
    // Distinguish between "our own previous mount" (e.g. Framer hot-reload
    // re-ran the loader) and "host page happens to use the same id". The
    // R5-10 collision fallback was treating BOTH as a host collision and
    // mounting a second widget on a random-suffix id — so on a re-init
    // the user saw TWO widgets side by side.
    const isOurs =
      existingRoot.hasAttribute('data-accessify-root') ||
      !!existingRoot.shadowRoot ||
      !!existingRoot.querySelector('#accessify-mount');
    if (isOurs) {
      // This is a redundant init() — same widget script being executed
      // twice (loader race, HMR, etc.). Skip silently; the existing
      // mount keeps running.
      console.info('[Accessify] init() skipped — widget already mounted.');
      return;
    }
    // Genuine host-DOM collision (rare). Random suffix as before.
    rootId = 'accessify-root-' + Math.random().toString(36).slice(2, 8);
    console.warn(
      '[Accessify] #accessify-root was already present in the host DOM; using ' +
        rootId + ' instead.'
    );
  }
  containerEl.id = rootId;
  containerEl.setAttribute('data-accessify-root', 'true');
  containerEl.setAttribute('role', 'complementary');
  containerEl.setAttribute('aria-label', t('widget.title', config.lang));
  // Put the host into the natural tab order. `delegatesFocus: true` on the
  // shadow root then forwards focus to the first focusable descendant (the
  // trigger button). Without this, Chromium skips the entire widget when
  // tabbing through the page — a hard accessibility blocker for an a11y
  // widget. See also injectSkipLink() above.
  containerEl.setAttribute('tabindex', '0');
  // Inline styles = highest specificity — can't be overridden by page CSS.
  // Host is a positioned container at the trigger position (bottom-right by default).
  // Trigger is static inside this container, Panel remains position:fixed.
  const pos = config.position || 'bottom-right';
  const isRight = pos.includes('right');
  const isBottom = pos.includes('bottom');
  const z = config.zIndex || 2147483647;
  // R15-2 Fix: individuelle setProperty-Calls statt cssText —
  // CSP `style-src 'self'` blockt sonst das gesetzte style-Attribut.
  const _styleProps: Array<[string, string]> = [
    ['position', 'fixed'],
    [isBottom ? 'bottom' : 'top', '20px'],
    [isRight ? 'right' : 'left', '20px'],
    ['z-index', String(z)],
    ['width', 'auto'],
    ['height', 'auto'],
    ['overflow', 'visible'],
    ['contain', 'none'],
    ['display', 'block'],
  ];
  for (const [prop, val] of _styleProps) {
    containerEl.style.setProperty(prop, val, 'important');
  }

  const shadow = containerEl.attachShadow({ mode: 'open', delegatesFocus: true });

  const sheet = new CSSStyleSheet();
  sheet.replaceSync(createWidgetStyles(config));
  shadow.adoptedStyleSheets = [sheet];

  const mountPoint = document.createElement('div');
  mountPoint.id = 'accessify-mount';
  shadow.appendChild(mountPoint);

  widgetInstance = mount(WidgetApp, {
    target: mountPoint,
    props: { config, shadow },
  });

  // Svelte 5 event delegation fix for Shadow DOM
  function findEventsSym(el: any): symbol | undefined {
    return Object.getOwnPropertySymbols(el).find(s => s.toString() === 'Symbol(events)');
  }
  // `pointerup` + `touchend` added in R11-1: on real iOS / Android Safari
  // synthetic `click` is unreliable across Shadow-DOM boundaries (browsers
  // suppress the click after a touchend on some elements), so the trigger
  // never reacts to a finger tap. Delegating pointerup/touchend lets the
  // Svelte-5-bound onclick handlers fire on the same gesture.
  const DELEGATED_EVENTS = ['click', 'pointerup', 'touchend', 'input', 'change', 'keydown', 'keyup', 'focusin', 'focusout'];
  for (const evt of DELEGATED_EVENTS) {
    shadow.addEventListener(evt, (e: Event) => {
      const path = e.composedPath();
      let node = path[0] as any;
      let handled = false;
      while (node && node !== shadow) {
        const sym = findEventsSym(node);
        if (sym) {
          const handlers = node[sym];
          if (handlers && typeof handlers[evt] === 'function') {
            handlers[evt].call(node, e);
            handled = true;
          }
        }
        if (e.cancelBubble) break;
        node = node.assignedSlot || node.parentNode || (node as ShadowRoot).host;
      }
      if (handled) e.stopImmediatePropagation();
    }, true);
  }

  // Listen for reposition events (legacy)
  window.addEventListener('accessify:reposition', ((e: CustomEvent) => {
    const newPos = e.detail?.position;
    if (newPos && config) {
      config.position = newPos;
      sheet.replaceSync(createWidgetStyles(config));
    }
  }) as EventListener);

  // EyeAble-style self-dismiss: the footer dispatches this only AFTER the
  // visitor confirms the explanatory prompt (PanelFooter owns that step). We
  // hide the whole host (trigger + panel) and persist the choice; the skip-link
  // teardown happens inside setWidgetHidden. Inert in the Dashboard preview so
  // the owner can see the control without blanking their preview.
  window.addEventListener('accessify:hide', () => {
    // Inert in preview. `isPreviewMode` only flips once a preview-config
    // message arrives, so it can still be false at init; `isPreviewContext()`
    // is synchronous (reads the proxy-injected __ACCESSIFY_PREVIEW_URL) and is
    // already true here — gate on both so an early click never blanks the
    // preview.
    if (isPreviewMode || isPreviewContext()) return;
    setWidgetHidden(true);
    // Sichtbare Meldung: das Widget ist weg, zurück geht es NUR über das
    // Löschen der Website-Daten im Browser (kein Alt+0 mehr).
    showHiddenToast();
  });

  // Config is already exposed via the getter defined on the Accessify function
  // (Object.defineProperty with get: () => config) — no manual assignment needed.

  // Listen for live-preview config updates from parent (dashboard iframe).
  //
  // Security: postMessage has no same-origin check by default, so any page
  // that embeds the widget (or any iframe on the page) could otherwise
  // rewrite the widget's config and re-inject CSS. We accept preview
  // messages ONLY from a hardcoded list of Accessify dashboard origins
  // (plus localhost for dev). Everything else is silently ignored.
  const ALLOWED_PREVIEW_ORIGINS = [
    'https://app.goaccessify.de',     // neues Dashboard (Cutover)
    'https://goaccessify.de',
    'https://www.goaccessify.de',
    'https://accessify-dashboard.pages.dev',
    'https://accessify.dev',
    'https://www.accessify.dev',
    'http://localhost:3000',
    'http://localhost:4321',
    'http://localhost:5173',
    'http://localhost:8787',
    'http://127.0.0.1:3000',
    'http://127.0.0.1:4321',
    'http://127.0.0.1:5173',
  ];
  function isAllowedPreviewOrigin(origin: string): boolean {
    if (ALLOWED_PREVIEW_ORIGINS.includes(origin)) return true;
    // also accept any *.goaccessify.de / *.accessify.dev / *.accessify-dashboard.pages.dev
    try {
      const u = new URL(origin);
      if (u.hostname.endsWith('.goaccessify.de')) return true;
      if (u.hostname.endsWith('.accessify.dev')) return true;
      if (u.hostname.endsWith('.accessify-dashboard.pages.dev')) return true;
    } catch {}
    return false;
  }
  window.addEventListener('message', (e: MessageEvent) => {
    if (!isAllowedPreviewOrigin(e.origin)) return;
    if (e.data?.type === 'accessify:preview-config') {
      // We're inside the Dashboard live-preview iframe. The hide feature is
      // excluded from the preview entirely: the Dashboard forces
      // allowVisitorHide=false in the posted config (so no hide button renders),
      // and here we additionally make sure the preview can never STAY hidden —
      // a previously-hidden preview (or a stale flag in this iframe origin) is
      // re-shown on every config message so the owner never gets locked out.
      isPreviewMode = true;
      if (isWidgetHidden()) setWidgetHidden(false);
      const preview = e.data.config || {};
      // R15: erkennen, ob sich Properties geändert haben, die NICHT rein
      // CSS-getrieben sind — also alles, was die Svelte-Tree-Struktur
      // ändert (features, lang). CSS-Sachen (theme, Farben, position,
      // panelWidth, compact) gehen weiterhin allein über sheet.replaceSync,
      // weil sie als CSS-Variablen reaktiv sind. Bei Feature-/Lang-Wechseln
      // muss aber neu gemountet werden, weil `$derived(new Set(config.features))`
      // in FeatureGrid.svelte nur auf $state-Mutationen reagiert — wir
      // tragen den Config aber als plainen Object herein, deshalb sieht
      // Svelte die Object.assign-Mutation nicht.
      const featuresChanged =
        Array.isArray(preview.features) &&
        JSON.stringify(preview.features) !== JSON.stringify(config.features);
      const langChanged =
        typeof preview.lang === 'string' && preview.lang !== config.lang;
      // The footer hide button is rendered from config at mount time (not a CSS
      // var), so flipping the owner toggle needs a re-mount to show/hide it.
      const hideChanged =
        typeof preview.allowVisitorHide === 'boolean' &&
        preview.allowVisitorHide !== (config as any).allowVisitorHide;
      // Merge preview overrides into current config
      Object.assign(config, preview);
      sheet.replaceSync(createWidgetStyles(config));
      // data-theme NEU setzen: der Preview-Handler spritzt nur das CSS neu,
      // ruft aber nicht die Svelte-applyTheme() auf. Ohne das behält das
      // Mount-Zeitpunkt-data-theme die Oberhand — bei einem Owner mit
      // Dark-OS bleibt es "dark", und #accessify-mount[data-theme="dark"]
      // (Spezifität 1,1,0) schlägt die hellen Marken-Farben (1,0,0). Ergebnis:
      // die Vorschau war dunkel, obwohl die Config hell ist. Auf der Live-Seite
      // tritt das nicht auf, weil dort direkt mit der finalen theme-Config
      // gemountet wird. Wir spiegeln hier das aufgelöste Theme aufs Mount.
      const previewMount = shadow.getElementById('accessify-mount');
      if (previewMount) {
        const t =
          config.theme === 'light' || config.theme === 'dark'
            ? config.theme
            : window.matchMedia('(prefers-color-scheme: dark)').matches
              ? 'dark'
              : 'light';
        previewMount.setAttribute('data-theme', t);
      }
      if ((featuresChanged || langChanged || hideChanged) && widgetInstance) {
        // Panel offen? Dann nach dem Re-Mount wieder öffnen, sonst springt
        // der User in der Vorschau jedes Mal auf den Trigger zurück.
        const wasOpen = !!shadow.querySelector(
          '.accessify-panel[aria-hidden="false"]',
        );
        unmount(widgetInstance);
        widgetInstance = mount(WidgetApp, {
          target: mountPoint,
          props: { config, shadow },
        });
        if (wasOpen) {
          // Re-open through the component's own API so isOpen (and with it the
          // scrim, the ESC handler and the X button's close()) stay in sync.
          // Poking aria-hidden directly left isOpen=false, so the next close()
          // was a no-op and the panel could never be closed again.
          widgetInstance.open();
        }
      }
    }
    if (e.data?.type === 'accessify:open') {
      // Open via the component API so isOpen stays in sync (see the remount note
      // above) and the panel can be closed again afterwards.
      widgetInstance?.open();
    }
  });

  document.body.appendChild(containerEl);

  // R11-2: remove the loader's CSS-only skeleton placeholder now that the
  // real trigger is in the DOM.
  document.getElementById('accessify-trigger-skeleton')?.remove();
  document.getElementById('accessify-skeleton-style')?.remove();

  // EyeAble-style self-dismiss: honor a prior "hide widget for me" choice —
  // but only while the owner still offers the capability. If they switched it
  // off (allowVisitorHide === false), clear the stale flag so the widget
  // reappears for everyone. Default (undefined) = capability on.
  //
  // CRITICAL preview carve-out: the Dashboard live-preview runs every site on
  // the SHARED api.goaccessify.de origin, so the per-origin `accessify-hidden`
  // flag is global to all previews. One visitor-hide click (or a stale flag in
  // that origin) would otherwise blank EVERY site's preview at init — the
  // widget would simply not appear ("kein Widget zu sehen"). The hide button is
  // inert in preview anyway (see the preview-config handler), so here we never
  // start hidden and clear any stale flag. Outside preview: unchanged.
  const inPreview = isPreviewContext();
  const hideAllowed = !inPreview && (config as any).allowVisitorHide !== false;
  if (isWidgetHidden()) setWidgetHidden(inPreview ? false : hideAllowed);
  const startHidden = hideAllowed && isWidgetHidden();

  // Insert the skip-link AFTER the container is in the DOM so the click
  // handler can resolve the trigger via getElementById on first focus.
  // injectSkipLink also wires up a MutationObserver that re-inserts the
  // link if Framer / React-driven hosts re-render the body — without
  // that, R9-1 reproduced 80 tabs without ever reaching the widget.
  // Skipped when the widget starts hidden: a "skip to accessibility settings"
  // link that opens nothing would be a dead affordance.
  if (!startHidden) {
    // Skip-Link bleibt der Tastatur-Einstieg zum Panel. Den früheren globalen
    // Alt+0/Alt+1-Shortcut gibt es nicht mehr: er war browserabhängig
    // unzuverlässig und konnte ein ausgeblendetes Widget ohnehin nie zurückholen.
    injectSkipLink(config.lang);
  }

  // R8-5 / R11-3: kick off the lazy-locale fetch if the requested
  // language isn't bundled (i.e. anything outside DE/EN). The widget
  // renders in English meanwhile, then re-renders via the
  // `accessify:locale-loaded` event when the proxy responds. Failures
  // fall back to English silently.
  if (config.lang && !isLocaleReady(config.lang)) {
    loadLocale(config.lang, config.proxyUrl).catch(() => {});
  }

  // Auto-apply any cached alt texts in the background. We defer this off
  // the critical render path because R7-2 observed it firing a /v1/manifest
  // request before the page was even visible — on a slow connection that
  // is a 1-3 s render delay for a feature most visitors never use. We use
  // requestIdleCallback where available, with a setTimeout(1500) fallback
  // for Safari (which still does not ship rIC).
  const runAutoApply = () => { autoApplyCachedAltTexts(config).catch(() => {}); };
  if (typeof (window as any).requestIdleCallback === 'function') {
    (window as any).requestIdleCallback(runAutoApply, { timeout: 4000 });
  } else {
    setTimeout(runAutoApply, 1500);
  }

  config.onReady?.();
}

function destroy() {
  if (widgetInstance) { unmount(widgetInstance); widgetInstance = null; }
  containerEl?.remove();
  containerEl = null;
}

function exec(command: string, ...args: any[]) {
  const commands: Record<string, Function> = {
    init: (cfg: any) => init(cfg),
    open: () => widgetInstance?.open?.(),
    close: () => widgetInstance?.close?.(),
    destroy,
  };

  commands[command]?.(...args);
}

(window as any).__accessify_exec = exec;
const api = (window as any).Accessify;

if (api?.q) {
  api.q.forEach((args: any[]) => {
    const [cmd, ...rest] = args;
    if (typeof cmd === 'string') exec(cmd, ...rest);
  });
  api.q = [];
}
const finalApi = function(cmd: string, ...args: any[]) { exec(cmd, ...args); } as any;
finalApi._loaded = true;
// Preserve config reference set by init()
Object.defineProperty(finalApi, 'config', {
  get: () => config,
  enumerable: true,
});
(window as any).Accessify = finalApi;

// Backward compatibility — `A11yPlus` is the legacy global from pre-rebrand
// integration snippets that are still embedded in some old customer pages.
// A getter rather than a direct assignment so each first-access logs a
// deprecation warning. The setter falls back to a normal assignment so
// later code (or tests) can still overwrite it without throwing.
let a11yPlusWarned = false;
Object.defineProperty(window as any, 'A11yPlus', {
  configurable: true,
  get() {
    if (!a11yPlusWarned) {
      a11yPlusWarned = true;
      console.warn(
        '[Accessify] window.A11yPlus is deprecated and will be removed in a ' +
          'future release. Replace it with window.Accessify.'
      );
    }
    return (window as any).Accessify;
  },
  set(value) {
    a11yPlusWarned = true;
    Object.defineProperty(window as any, 'A11yPlus', { value, writable: true, configurable: true });
  },
});

/**
 * Live read-through to the active widget config. Use this from `widget-entry`
 * so the Accessify global keeps a working `config` getter even after the
 * queue API has been swapped out for the direct exec function — R9-4
 * surfaced that the direct API was missing the getter on production.
 */
export function getActiveConfig(): AccessifyConfig {
  return config;
}

export { init, destroy };
export type { AccessifyConfig };
